import React, { useState, useEffect, useCallback } from "react";
import "./styles.css"; // Import the custom styles
import { COLORS } from "../../utils/colors";
import DualSlider from "./dual-slider";
import { BarCharts } from "./chart";
import { CompareProperty } from "../../pages/property/compare-property";

// Helper functions (can be moved to a separate utils.js file if preferred)
const formatCurrency = (value) => {
  return "$" + formatNumber(value);
};

const formatNumber = (value, decimals = 0) => {
  if (isNaN(value) || !isFinite(value)) {
    return decimals > 0 ? (0).toFixed(decimals) : "0";
  }
  return value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

function HouseFlippingCalculator(props) {
  const {
    buildingSquareFeet = 1500,
    estimatedRentAmount = 1800,
    estimatedValue = 700000,
    loanBalance = 200000,
    lotSquareFeet = 5000,
    marketValuePerSqft = 400,
    setMarketValuePerSqft = () => {},
    pool = false,
    property,
  } = props;
  const [activeTab, setActiveTab] = useState("input"); // 'input' or 'results'

  // Input States
  const [presentValue, setPresentValue] = useState(estimatedValue);
  const [homeArea, setHomeArea] = useState(buildingSquareFeet);
  const [loanAmount, setLoanAmount] = useState(loanBalance);
  const [currentRental, setCurrentRental] = useState(estimatedRentAmount);
  const [lotSize, setLotSize] = useState(lotSquareFeet);
  const [hasPool, setHasPool] = useState(pool ? "yes" : "no");
  const [renovationTier, setRenovationTier] = useState(200);
  const [extraAreaTier, setExtraAreaTier] = useState(400);
  const [addAreaCheckbox, setAddAreaCheckbox] = useState(false);
  const [extraArea, setExtraArea] = useState(0);
  const [investorType, setInvestorType] = useState(8);
  const [rentalPeriod, setRentalPeriod] = useState(12);

  const [profitValues, setProfitValues] = useState([75, 85]); // A: 0-30, B: 30-70, C: 70-100

  const [profitA, profitB, profitC] = [
    profitValues[0],
    profitValues[1] - profitValues[0],
    100 - profitValues[1],
  ];

  // Results States
  const [results, setResults] = useState({
    presentValue: 0,
    currentPriceSqft: 0,
    totalArea: 0,
    loan: 0,
    renovationTierText: "",
    renovationCost: 0,
    extraAreaAdded: 0,
    extraAreaCost: 0,
    upfrontRental: 0,
    constructionLoan: 0,
    loanInterest: 0,
    agentCommission: 0,
    closingCosts: 0,
    marketValue: 0,
    estimatedSalePrice: 0,
    totalInvestment: 0,
    totalProfit: 0,
    roi: 0,
    profitMargin: 0,
    ownerProfit: 0,
    investorProfit: 0,
    agentProfit: 0,
  });

  const calculateResults = useCallback(() => {
    const pv = parseFloat(presentValue) || 0;
    const ha = parseFloat(homeArea) || 0;
    const la = parseFloat(loanAmount) || 0;
    const cr = parseFloat(currentRental) || 0;
    const rt = parseInt(renovationTier);
    const ea = addAreaCheckbox ? parseFloat(extraArea) || 0 : 0;
    const it = parseInt(investorType);
    const rp = parseInt(rentalPeriod);
    const mvpsf = parseFloat(marketValuePerSqft) || 0;

    const renovationCostPerSqft = rt;
    const currentRenovationCost = ha * renovationCostPerSqft;
    const currentExtraAreaCost = ea * extraAreaTier;
    const totalConstructionCost = currentRenovationCost + currentExtraAreaCost;

    const currentUpfrontRental = cr * rp;
    const interestRate = (it * rp) / 1200;
    const currentTotalLoan = totalConstructionCost + currentUpfrontRental;
    const currentLoanInterest = currentTotalLoan * interestRate;

    const totalAreaResult = ha + ea;
    const currentEstimatedSalePrice = totalAreaResult * mvpsf;
    const currentAgentCommission = currentEstimatedSalePrice * 0.06;
    const currentClosingCosts = currentEstimatedSalePrice * 0.02;

    const currentTotalInvestment =
      pv +
      totalConstructionCost +
      currentLoanInterest +
      currentAgentCommission +
      currentClosingCosts +
      currentUpfrontRental;
    const currentTotalProfit =
      currentEstimatedSalePrice - currentTotalInvestment;
    const currentRoi =
      currentTotalInvestment !== 0
        ? (currentTotalProfit / currentTotalInvestment) * 100
        : 0;
    const currentProfitMargin =
      currentEstimatedSalePrice !== 0
        ? (currentTotalProfit / currentEstimatedSalePrice) * 100
        : 0;

    const currentOwnerProfit = (currentTotalProfit * profitA) / 100;
    // Owner gets 50% of profit, but can be adjusted based on profitValues
    const currentInvestorProfit = (currentTotalProfit * profitB) / 100;
    // Investor gets 25% of profit, but can be adjusted based on profitValues
    const currentAgentProfit = (currentTotalProfit * profitC) / 100;
    // Agent gets 25% of profit, but can be adjusted based on profitValues

    const renovationTierTextMap = {
      1: "Tier 1 (Basic)",
      2: "Tier 2 (Standard)",
      3: "Tier 3 (Complete)",
      4: "Tier 4 (Premium)",
    };

    setResults({
      presentValue: pv,
      currentPriceSqft: ha !== 0 ? pv / ha : 0,
      totalArea: totalAreaResult,
      loan: la,
      renovationTierText: renovationTierTextMap[rt],
      renovationCost: currentRenovationCost,
      totalLoan: currentTotalLoan,
      extraAreaAdded: ea,
      extraAreaCost: currentExtraAreaCost,
      upfrontRental: currentUpfrontRental,
      constructionLoan: totalConstructionCost,
      loanInterest: currentLoanInterest,
      agentCommission: currentAgentCommission,
      closingCosts: currentClosingCosts,
      marketValue: mvpsf,
      estimatedSalePrice: currentEstimatedSalePrice,
      rentalPeriod: rentalPeriod,
      totalInvestment: currentTotalInvestment,
      totalProfit: currentTotalProfit,
      roi: currentRoi,
      profitMargin: currentProfitMargin,
      ownerProfit: currentOwnerProfit,
      investorProfit: currentInvestorProfit,
      agentProfit: currentAgentProfit,
    });
  }, [
    presentValue,
    homeArea,
    loanAmount,
    currentRental,
    renovationTier,
    addAreaCheckbox,
    extraArea,
    investorType,
    rentalPeriod,
    marketValuePerSqft,
    profitValues,
  ]);

  useEffect(() => {
    // Initial calculation on load, if needed, or wait for button click
    calculateResults();
  }, [calculateResults]); // Recalculate if any dependency changes

  useEffect(() => {
    if (!addAreaCheckbox) {
      setExtraArea(0);
    }
  }, [addAreaCheckbox]);

  const handleCalculateClick = () => {
    calculateResults();
    setActiveTabFn("results");
  };

  const setActiveTabFn = (tab) => {
    setActiveTab(tab);
    activeTab && window.scrollTo(0, 640); // values are x,y-offset
  };
  const renovationTierDisplay =
    renovationTier > 499
      ? "Premium Renovation"
      : renovationTier > 399
      ? "Complete Renovation"
      : renovationTier > 299
      ? "Standard Renovation"
      : "Basic Renovation";

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8 app-container">
      {" "}
      {/* Added app-container for body styles */}
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            House Flipping Calculator
          </h1>
          <p className="text-lg text-gray-600">
            Analyze your property's potential and maximize your profits
          </p>
        </div>

        <div className="calculator-container rounded-xl p-6 sm:p-8">
          <div className="mb-8">
            <div className="flex border-b">
              <button
                onClick={() => setActiveTabFn("input")}
                className={`tab px-4 py-3 text-lg flex-1 text-center ${
                  activeTab === "input" ? "active" : ""
                }`}
              >
                Property Details
              </button>
              <button
                onClick={() => setActiveTabFn("results")}
                className={`tab px-4 py-3 text-lg flex-1 text-center ${
                  activeTab === "results" ? "active" : ""
                }`}
              >
                Results
              </button>
            </div>
          </div>

          {activeTab === "input" && (
            <div id="input-section">
              {/* Property Details Section */}
              <div className="bg-blue-50 p-5 rounded-lg mb-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                  Current Property Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="input-group">
                    <label
                      htmlFor="present-value"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Present Value of Home ($)
                    </label>
                    <input
                      type="number"
                      id="present-value"
                      value={presentValue}
                      onChange={(e) => setPresentValue(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 text-gray-500 rounded-md focus:outline-none"
                    />
                    <p
                      id="present-value-formatted"
                      className="text-sm text-700 mt-1"
                    >
                      ${Number(presentValue).toLocaleString()} ($
                      {Math.ceil(
                        Number(presentValue) / Number(homeArea)
                      ).toLocaleString()}{" "}
                      per sq ft)
                    </p>
                  </div>
                  <div className="input-group">
                    <label
                      htmlFor="home-area"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Home Area (sq ft)
                    </label>
                    <input
                      type="number"
                      id="home-area"
                      value={homeArea}
                      onChange={(e) => setHomeArea(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 text-gray-500 rounded-md focus:outline-none"
                    />
                  </div>
                  <div className="input-group">
                    <label
                      htmlFor="loan-amount"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Open Loan Balance ($)
                    </label>
                    <input
                      type="number"
                      id="loan-amount"
                      value={loanAmount}
                      onChange={(e) => setLoanAmount(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 text-gray-500 rounded-md focus:outline-none"
                    />
                    <p
                      id="loan-amount-formatted"
                      className="text-sm text-700 mt-1"
                    >
                      ${Number(loanAmount).toLocaleString()}
                    </p>
                  </div>
                  <div className="input-group">
                    <label
                      htmlFor="current-rental"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Current Estimated Monthly Rental ($)
                    </label>
                    <input
                      type="number"
                      id="current-rental"
                      value={currentRental}
                      onChange={(e) => setCurrentRental(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 text-gray-500 rounded-md focus:outline-none"
                    />
                    <p
                      id="current-rental-formatted"
                      className="text-sm text-700 mt-1"
                    >
                      ${Number(currentRental).toLocaleString()}
                    </p>
                  </div>
                  <div className="input-group">
                    <label
                      htmlFor="lot-size"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Lot Size (sq ft)
                    </label>
                    <input
                      type="number"
                      id="lot-size"
                      value={lotSize}
                      onChange={(e) => setLotSize(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 text-gray-500 rounded-md focus:outline-none"
                    />
                  </div>
                  <div className="input-group">
                    <label
                      htmlFor="has-pool"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Pool
                    </label>
                    <select
                      id="has-pool"
                      value={hasPool}
                      onChange={(e) => setHasPool(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 text-gray-500 rounded-md focus:outline-none"
                    >
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Renovation Options Section */}
              <div className="bg-blue-50 p-5 rounded-lg mb-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                  Renovation Options
                </h2>
                <div className="mb-6">
                  <label
                    htmlFor="renovation-tier"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Renovation of Existing Structure - ({formatNumber(homeArea)}
                    sq. ft.) - (Upgrade or remodel an existing building.)
                  </label>
                  <div className="flex items-center">
                    <input
                      type="range"
                      id="renovation-tier-slider"
                      className="slider w-full mr-4"
                      min="100"
                      max="1000"
                      step="10"
                      value={renovationTier}
                      onChange={(e) =>
                        setRenovationTier(parseInt(e.target.value))
                      }
                    />
                    <span
                      id="renovation-tier-display"
                      className="text-lg font-semibold text-blue-600 min-w-[150px] text-right"
                    >
                      ${renovationTier} per sq. ft.
                    </span>
                  </div>
                  <label
                    htmlFor="renovation-tier-display"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {renovationTierDisplay}
                  </label>
                </div>
                <div className="mb-6">
                  <div className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      id="add-area-checkbox"
                      checked={addAreaCheckbox}
                      onChange={(e) => setAddAreaCheckbox(e.target.checked)}
                      className="mr-2 h-5 w-5 text-blue-600"
                    />
                    <label
                      htmlFor="add-area-checkbox"
                      className="text-sm font-medium text-gray-700"
                    >
                      Add Extra Area - Build entirely new structure apart from
                      existing area
                    </label>
                  </div>
                  {addAreaCheckbox && (
                    <div>
                      <div className="mb-4" id="extra-area-input">
                        <label
                          htmlFor="extra-area"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Extra Area to Build (sq ft)
                        </label>
                        <input
                          type="number"
                          id="extra-area"
                          value={extraArea}
                          onChange={(e) => setExtraArea(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 text-gray-500 rounded-md focus:outline-none"
                        />
                      </div>
                      <div className="mb-1">
                        <label
                          htmlFor="extra-area-tier"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Extra Area Cost
                        </label>
                        <div className="flex items-center">
                          <input
                            type="range"
                            id="extra-area-tier-slider"
                            className="slider w-full mr-4"
                            min="300"
                            max="1000"
                            step="10"
                            value={extraAreaTier}
                            onChange={(e) =>
                              setExtraAreaTier(parseInt(e.target.value))
                            }
                          />
                          <span
                            id="extra-area-tier-display"
                            className="text-lg font-semibold text-blue-600 min-w-[150px] text-right"
                          >
                            ${extraAreaTier} per sq. ft.
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <p id="renovation-total" className="text-sm text-700 mt-1">
                  ${Number(results.constructionLoan).toLocaleString()} total
                  renovation cost - Total Area:{" "}
                  {formatNumber(parseInt(homeArea) + parseInt(extraArea))} sq ft
                  {extraArea > 0
                    ? ` (Existing Construction ${formatNumber(
                        homeArea
                      )} sq ft + New Construction ${formatNumber(
                        extraArea
                      )} sq ft)`
                    : ""}
                </p>
              </div>

              {/* Financing Options Section */}
              <div className="bg-blue-50 p-5 rounded-lg mb-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                  Financing Options
                </h2>
                <div className="mb-6">
                  <label
                    htmlFor="investor-type"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Investor Return - Annually
                  </label>
                  <div className="flex items-center">
                    <input
                      type="range"
                      id="investor-type-slider"
                      className="slider w-full mr-4"
                      min="8"
                      max="30"
                      step="1"
                      value={investorType}
                      onChange={(e) =>
                        setInvestorType(parseInt(e.target.value))
                      }
                    />
                    <span
                      id="extra-area-tier-display"
                      className="text-lg font-semibold text-blue-600 min-w-[150px] text-right"
                    >
                      {investorType}% Interest p.a.
                    </span>
                  </div>
                </div>
                <div className="mb-6">
                  <label
                    htmlFor="rental-period"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Upfront Rental Period / Renovation Period
                  </label>
                  <div className="flex items-center">
                    <input
                      type="range"
                      id="rental-period"
                      className="slider w-full mr-4"
                      min="6"
                      max="36"
                      step="1"
                      value={rentalPeriod}
                      onChange={(e) =>
                        setRentalPeriod(parseInt(e.target.value))
                      }
                    />
                    <span
                      id="extra-area-tier-display"
                      className="text-lg font-semibold text-blue-600 min-w-[150px] text-right"
                    >
                      {rentalPeriod} Months
                    </span>
                  </div>
                </div>
              </div>

              {/* Market Analysis Section */}
              <div className="bg-blue-50 p-5 rounded-lg mb-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                  Market Analysis
                </h2>
                <div className="mb-4">
                  <label
                    htmlFor="market-value-slider"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Similar Renovated Properties Value ($ per sq ft)
                  </label>
                  <div className="flex items-center">
                    <input
                      type="range"
                      id="market-value-slider"
                      className="slider w-full mr-4"
                      min="200"
                      max="10000"
                      step="10"
                      value={marketValuePerSqft}
                      onChange={(e) => setMarketValuePerSqft(e.target.value)}
                    />
                    <span
                      id="market-value-display"
                      className="text-lg font-semibold text-blue-600 min-w-[180px] text-right"
                    >
                      ${marketValuePerSqft} per sq. ft.
                    </span>
                  </div>
                  <div className="mt-8 flex justify-center">
                    <CompareProperty property={property} />
                  </div>
                </div>
              </div>

              <div className="text-center">
                <button
                  onClick={handleCalculateClick}
                  className=" text-white font-medium py-3 px-8 rounded-lg transition duration-300 transform hover:scale-105"
                  style={{ backgroundColor: COLORS.SURFACE_1 }}
                >
                  Calculate Profit Potential
                </button>
              </div>
            </div>
          )}

          {activeTab === "results" && (
            <div id="results-section">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="result-card bg-white p-5 rounded-lg shadow-md">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">
                    Property Details
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Present Value:</span>
                      <span className="font-medium">
                        {formatCurrency(results.presentValue)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        Current Price per sq ft:
                      </span>
                      <span className="font-medium">
                        {formatCurrency(results.currentPriceSqft)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Area:</span>
                      <span className="font-medium">
                        {formatNumber(results.totalArea)} sq ft
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Current Loan:</span>
                      <span className="font-medium">
                        {formatCurrency(results.loan)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="result-card bg-white p-5 rounded-lg shadow-md">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">
                    Renovation Details
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Renovation Type:</span>
                      <span className="font-medium">
                        {renovationTierDisplay} - ${renovationTier} per sq ft
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Renovation Cost:</span>
                      <span className="font-medium">
                        {formatCurrency(results.renovationCost)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Extra Area Added:</span>
                      <span className="font-medium">
                        {formatNumber(results.extraAreaAdded)} sq ft
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        Extra Area Renovation Type:
                      </span>
                      <span className="font-medium">
                        ${formatNumber(extraAreaTier)} per sq ft
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Extra Area Cost:</span>
                      <span className="font-medium">
                        {formatCurrency(results.extraAreaCost)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        Total Area after renovation:
                      </span>
                      <span className="font-medium">
                        {formatCurrency(results.totalArea)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="result-card bg-white p-5 rounded-lg shadow-md">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">
                    Financing Details
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        Upfront Rental Payment:
                      </span>
                      <span className="font-medium">
                        {formatCurrency(results.upfrontRental)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Construction Loan:</span>
                      <span className="font-medium">
                        {formatCurrency(results.constructionLoan)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Loan Given:</span>
                      <span className="font-medium">
                        {formatCurrency(
                          results.constructionLoan + results.upfrontRental
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Loan Interest:</span>
                      <span className="font-medium">
                        {formatCurrency(results.loanInterest)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        Agent Commission (6%):
                      </span>
                      <span className="font-medium">
                        {formatCurrency(results.agentCommission)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Closing Costs (2%):</span>
                      <span className="font-medium">
                        {formatCurrency(results.closingCosts)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="result-card bg-white p-5 rounded-lg shadow-md">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">
                    Market Analysis
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        Market Value (per sq ft):
                      </span>
                      <span className="font-medium">
                        {formatCurrency(results.marketValue)}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-gray-600">Current Value:</span>
                      <span className="font-medium">
                        {formatCurrency(estimatedValue)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        Total Flipping Cost:
                      </span>
                      <span className="font-medium">
                        {formatCurrency(
                          results.totalInvestment - estimatedValue
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total:</span>
                      <span className="font-medium">
                        {formatCurrency(results.totalInvestment)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        Estimated Sale Price:
                      </span>
                      <span className="font-medium color-primary text-700">
                        {formatCurrency(results.estimatedSalePrice)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg shadow-md mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  Profit Summary
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="text-center">
                      <p className="text-gray-600 mb-1">Total Profit</p>
                      <p
                        className={`text-2xl font-bold ${
                          results.totalProfit >= 0
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {formatCurrency(results.totalProfit)}
                      </p>
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="text-center">
                      <p className="text-gray-600 mb-1">ROI</p>
                      <p
                        className={`text-2xl font-bold ${
                          results.roi >= 0 ? "text-blue-600" : "text-red-600"
                        }`}
                      >
                        {formatNumber(results.roi, 1)}%
                      </p>
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="text-center">
                      <p className="text-gray-600 mb-1">Profit Margin</p>
                      <p
                        className={`text-2xl font-bold ${
                          results.profitMargin >= 0
                            ? "text-purple-600"
                            : "text-red-600"
                        }`}
                      >
                        {formatNumber(results.profitMargin, 1)}%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Extra Proceeds Distribution
                </h3>
                <p className="text-xs font-semibold text-gray-800 mb-4">
                  Extra proceeds distribution is the allocation of leftover
                  profits after all costs, debts, and agreed returns have been
                  paid in a deal or investment.
                </p>
                <DualSlider values={profitValues} onChange={setProfitValues} />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center flex flex-col items-center">
                    <div
                      className={`inline-block w-32 h-32 rounded-full ${
                        results.ownerProfit >= 0 ? "bg-blue-100" : "bg-red-100"
                      } flex items-center justify-center mb-3`}
                    >
                      <div>
                        <p
                          className={`text-xl font-bold ${
                            results.ownerProfit >= 0
                              ? "text-blue-600"
                              : "text-red-600"
                          }`}
                        >
                          {formatCurrency(results.ownerProfit)}
                        </p>
                        <p className="text-sm text-gray-600">{profitA}%</p>
                      </div>
                    </div>
                    <p className="font-medium text-gray-800">Owner</p>
                  </div>
                  <div className="text-center flex flex-col items-center">
                    <div
                      className={`inline-block w-32 h-32 rounded-full ${
                        results.investorProfit >= 0
                          ? "bg-green-100"
                          : "bg-red-100"
                      } flex items-center justify-center mb-3`}
                    >
                      <div>
                        <p
                          className={`text-xl font-bold ${
                            results.investorProfit >= 0
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          {formatCurrency(results.investorProfit)}
                        </p>
                        <p className="text-sm text-gray-600">{profitB}%</p>
                      </div>
                    </div>
                    <p className="font-medium text-gray-800">Investor</p>
                  </div>
                  <div className="text-center flex flex-col items-center">
                    <div
                      className={`inline-block w-32 h-32 rounded-full ${
                        results.agentProfit >= 0
                          ? "bg-purple-100"
                          : "bg-red-100"
                      } flex items-center justify-center mb-3`}
                    >
                      <div>
                        <p
                          className={`text-xl font-bold ${
                            results.agentProfit >= 0
                              ? "text-purple-600"
                              : "text-red-600"
                          }`}
                        >
                          {formatCurrency(results.agentProfit)}
                        </p>
                        <p className="text-sm text-gray-600">{profitC}%</p>
                      </div>
                    </div>
                    <p className="font-medium text-gray-800">Developer</p>
                  </div>
                </div>
              </div>

              <BarCharts results={results}></BarCharts>

              <div className="text-center">
                <button
                  onClick={() => setActiveTabFn("input")}
                  className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-8 rounded-lg transition duration-300 transform hover:scale-105"
                >
                  Modify Inputs
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default HouseFlippingCalculator;
